import type { ReactNode } from 'react';
import React from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { FastField, Field, useFormikContext } from 'formik';
import { useAppContext } from 'Context/ReactAppContext';
import * as yup from 'yup';
import { getValidateFieldFunction } from './inputsUtils';
import { DateInput } from './formik-input/DateInput';
import { Checkbox } from './formik-input/Checkbox';
import { CreditCardExpiryInput } from './formik-input/CreditCardExpiryInput';
import { Datalist } from './formik-input/Datalist';
import { DatePickerInput } from './formik-input/DatePickerInput';
import { Hidden } from './formik-input/Hidden';
import { Input } from './formik-input/Input';
import { Radio } from './formik-input/Radio';
import { Select } from './formik-input/Select';
import { Textarea } from './formik-input/Textarea';
import { TelWithCodeInput } from './formik-input/TelWithCodeInput';

export type FormikInputProps = {
  customId?: string;
  dataTestId?: string;
  hideDataFromSessionRecording?: boolean;
  hideErrorMessage?: boolean;
  iconColor?: Colors;
  isDisabled?: boolean;
  isFlex?: boolean;
  isRequired?: boolean;
  isValidate?: boolean;
  label?: string | Node;
  labelAdditionalInfo?: string | Node;
  name: string;
  optimized?: boolean;
  type?:
    | 'default'
    | 'birthdate'
    | 'checkbox'
    | 'creditCardExpiry'
    | 'datalist'
    | 'datepicker'
    | 'email'
    | 'hidden'
    | 'number'
    | 'password'
    | 'radio'
    | 'select'
    | 'tel'
    | 'telWithCode'
    | 'text'
    | 'textarea';
  validate?: () => boolean;
};

const getComponentByType = (type) => {
  switch (type) {
    case 'birthdate':
      return DateInput;

    case 'checkbox':
      return Checkbox;

    case 'creditCardExpiry':
      return CreditCardExpiryInput;

    case 'datalist':
      return Datalist;

    case 'datepicker':
      return DatePickerInput;

    case 'hidden':
      return Hidden;

    case 'radio':
      return Radio;

    case 'select':
      return Select;
    case 'telWithCode':
      return TelWithCodeInput;

    case 'textarea':
      return Textarea;

    default:
      return Input;
  }
};

const requiredValidationRule = getValidateFieldFunction(
  yup
    .mixed()
    .transform((value) => !!value)
    .oneOf([true])
    .required(),
);

const getValidateFunction = (validate, isRequired, context, isValidate = true, locale) => {
  if (validate) {
    return getValidateFieldFunction(validate, context, locale);
  }

  if (isValidate) {
    if (isRequired) {
      return requiredValidationRule;
    }
  }

  return undefined;
};

export const FormikInput = ({
  customId,
  hideErrorMessage = false,
  hideDataFromSessionRecording = true,
  isRequired = false,
  isValidate = true,
  optimized = false,
  type = 'default',
  validate,
  ...otherProps
}: FormikInputProps) => {
  const { locale } = useAppContext();
  const context = useFormikContext();
  const FormikField = optimized ? FastField : Field;
  const air360ClassName = classnames({
    'air360-replay-ignore': hideDataFromSessionRecording,
  });
  const dataCsMask = hideDataFromSessionRecording
    ? {
        'data-cs-mask': true,
      }
    : undefined;
  return (
    <FormikField
      air360ClassName={air360ClassName}
      component={getComponentByType(type)}
      customId={customId}
      hideErrorMessage={hideErrorMessage}
      isRequired={isRequired}
      type={type}
      validate={getValidateFunction(validate, isRequired, context, isValidate, locale)}
      {...otherProps}
      {...dataCsMask}
    />
  );
};
